import { getRunId } from '@/shared/utils/api-utils';
import { truncateString } from '@/shared/utils/common-utils';

import type { TRainValueJs } from '../utils/value';

// Parsers for the results quill2's tools return — its build-loop test tools,
// and the `run_code` sandbox. Kept free of JSX so the automation surface can
// read a run id out of a tool result without pulling in the cards that render
// one.

/**
 * Pull the committed draft version off a decoded `save_automation` result.
 * The backend returns `version` as a string or number (see KOG-11802);
 * anything else yields undefined so the caller falls back to the generic card.
 */
export const extractSavedAutomationVersion = (
  result: TRainValueJs | undefined,
): string | undefined => {
  if (!result || typeof result !== 'object' || Array.isArray(result)) {
    return undefined;
  }
  const version = (result as { [key: string]: TRainValueJs }).version;
  if (typeof version === 'string' && version.length > 0) return version;
  if (typeof version === 'number') return String(version);
  return undefined;
};

/**
 * `test_automation` documents that it returns "a run name", and in practice
 * sends either a full resource name or a bare id — so a value with no path
 * separator is already the id.
 */
const toRunId = (value: string): string =>
  value.includes('/') ? getRunId(value) : value;

/**
 * Pull a run id out of a `test_automation`/`replay_run` result, which may be a
 * bare string or an object carrying the name under `run` / `run_name` / `name`.
 */
export function extractTestAutomationRunId(
  result: TRainValueJs | undefined,
): string | undefined {
  if (typeof result === 'string') return toRunId(result) || undefined;
  if (!result || typeof result !== 'object' || Array.isArray(result)) {
    return undefined;
  }
  const record = result as { [key: string]: TRainValueJs };
  for (const key of ['run', 'run_name', 'name']) {
    const value = record[key];
    if (typeof value === 'string') {
      const id = toRunId(value);
      if (id) return id;
    }
  }
  return undefined;
}

/**
 * Pulls the source run's id out of a `replay_run` result's `previous_run`
 * field (`V1Run.previous_run` — "the run that was replayed to produce this
 * run", set by the `ReplayRun` RPC so completed book calls can be replayed
 * from its recordings). Unlike `extractTestAutomationRunId`, this only reads
 * an object result's field — a bare-string result has no `previous_run` to
 * read, since the string itself is the *new* run's own name, not a nested
 * run body.
 *
 * Used to auto-focus a `replay_run`'s new run onto the step view without a
 * click, but only once its `previous_run` is confirmed to match whatever run
 * is already focused — see `useRainThreadMessages`'s `tool_result` handling.
 */
export function extractPreviousRunId(
  result: TRainValueJs | undefined,
): string | undefined {
  if (!result || typeof result !== 'object' || Array.isArray(result)) {
    return undefined;
  }
  const record = result as { [key: string]: TRainValueJs };
  for (const key of ['previous_run', 'previousRun']) {
    const value = record[key];
    if (typeof value === 'string' && value) {
      const id = toRunId(value);
      if (id) return id;
    }
  }
  return undefined;
}

/**
 * Pulls a `test_automation`/`replay_run` result's `create_time` field
 * (`V1Run.create_time`, an RFC 3339 string) — set once by jeeves when the
 * run is created and never updated afterward, unlike the same result's
 * `state`, which is only a snapshot from the trigger ack (the tool call
 * resolves once the run is *started*, not once it *finishes* — see
 * `<test_automation>`'s "returns a run name... poll with `get_run`"). `state`
 * goes stale the moment the run keeps executing past that snapshot;
 * `create_time` doesn't, since it's immutable run metadata, not live status.
 *
 * Preferred over the tool call's own `startedAtMs` (from the
 * `ToolCallStart` event's `recorded_at_ms`) for display: it's the run's own
 * authoritative clock rather than rain's event-log clock, and it's already
 * present in the same result object `extractTestAutomationRunId` reads.
 */
export function extractRunCreateTime(
  result: TRainValueJs | undefined,
): string | undefined {
  if (!result || typeof result !== 'object' || Array.isArray(result)) {
    return undefined;
  }
  const record = result as { [key: string]: TRainValueJs };
  for (const key of ['create_time', 'createTime']) {
    const value = record[key];
    if (typeof value === 'string' && value) return value;
  }
  return undefined;
}

/**
 * Pull the created collection's id out of a `create_collection` result, so the
 * approval card can link at the collection itself. The result is the stored
 * collection, whose `name` is a full resource path.
 */
export function extractCreatedCollectionId(
  result: TRainValueJs | undefined,
): string | undefined {
  if (!result || typeof result !== 'object' || Array.isArray(result)) {
    return undefined;
  }
  const name = (result as { [key: string]: TRainValueJs }).name;
  if (typeof name !== 'string') return undefined;
  return name.split('/').pop() || undefined;
}

// Sanity bound, not the visible limit: the row clamps in CSS and keeps the full
// string for its overflow tooltip.
const MAX_PURPOSE_CHARS = 240;

/**
 * `run_code`'s `purpose` — the only one of its arguments a person may see.
 * `undefined` means "no label yet": still streaming, blank, or an older agent.
 */
export function readRunCodePurpose(
  args: Record<string, TRainValueJs>,
): string | undefined {
  const value = args.purpose;
  if (typeof value !== 'string') return undefined;
  const line = value.replace(/\s+/g, ' ').trim();
  if (!line) return undefined;
  return truncateString(line, MAX_PURPOSE_CHARS, '…');
}

/** The six states quill2's jq flattens jarvis' `ExecutionStatus` oneof onto. */
const RUN_CODE_STATUSES = [
  'COMPLETED',
  'FAILED',
  'STOPPED',
  'STARTED',
  'IN_PROGRESS',
  'UNKNOWN',
] as const;

export type TRunCodeExecutionStatus = (typeof RUN_CODE_STATUSES)[number];

const isRunCodeStatus = (value: string): value is TRunCodeExecutionStatus =>
  (RUN_CODE_STATUSES as readonly string[]).includes(value);

/** What a `run_code` card needs off the tool result, and nothing more. */
export interface IRunCodeResult {
  /** Never rendered; it only keys the outputs fetch. */
  executionId?: string;
  executionStatus: TRunCodeExecutionStatus;
  /** A key count, never a read: the values in this payload are lossy. */
  hasSetOutputs: boolean;
}

const asRecord = (
  value: TRainValueJs | undefined,
): { [key: string]: TRainValueJs } | undefined =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as { [key: string]: TRainValueJs })
    : undefined;

const asNonEmptyString = (
  value: TRainValueJs | undefined,
): string | undefined =>
  typeof value === 'string' && value ? value : undefined;

/**
 * Reads a `run_code` result. `error` is deliberately not read: its description,
 * location and code are all SPy, so a failure reaches the card as a status only.
 */
export function parseRunCodeResult(
  result: TRainValueJs | undefined,
): IRunCodeResult {
  const record = asRecord(result);
  if (!record) return { executionStatus: 'UNKNOWN', hasSetOutputs: false };

  const status = asNonEmptyString(record.status);
  const outputs = asRecord(asRecord(record.result)?.outputs);

  return {
    executionId: asNonEmptyString(record.execution_id),
    executionStatus: status && isRunCodeStatus(status) ? status : 'UNKNOWN',
    hasSetOutputs: Object.keys(outputs ?? {}).length > 0,
  };
}
